
/* General utility functions
*  Can be used anywhere
*/

//Reference: https://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
export const uniqueID = () => (
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    let r = Math.random() * 16 | 0, v = c == "x" ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  })
);

//Removes the element from the array if it finds it
export const removeFromArr = (arr, val) => {
  let index = arr.indexOf(val);
  if (index !== -1) {
    return [
      ...arr.slice(0, index),
      ...arr.slice(index + 1),
    ];
  }
  return arr;
};

export const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

//25 September 2026
export const getDate = (date) => {
  if (!date) {
    return "";
  }
  const d = (date instanceof Date) ? date : new Date(date);
  return d.getDate() + " " + months[d.getMonth()] + " " + d.getFullYear();
};

//25 Sep 2026
export const getShortDate = (date) => {
  if (!date) {
    return "";
  }
  const d = (date instanceof Date) ? date : new Date(date);
  return d.getDate() + " " + months[d.getMonth()].slice(0, 3) + " " + d.getFullYear();
};

//Returns a date string in the format: DD Month YYYY hh:mm
export const getDateAndTime = (date) => {
  const d = (date instanceof Date) ? date : new Date(date);
  let h = d.getHours();
  let m = d.getMinutes();
  if (h < 10) {
    h = "0" + h;
  }
  if (m < 10) {
    m = "0" + m;
  }
  return getShortDate(d) + " " + h + ":" + m;
};

//R1 200 (whole rands) - pass keepCents for payment amounts
export const formatPrice = (num, keepCents=false) => {
  const value = Number(num) || 0;
  const [rands, cents] = value.toFixed(2).split(".");
  const withSpaces = rands.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return (keepCents && cents !== "00") ? `R${withSpaces}.${cents}` : `R${withSpaces}`;
};

//R400 / $25 - whole amounts unless keepCents and there are cents
export const formatMoney = (num, currency = "ZAR", keepCents = false) => {
  if (currency === "USD") {
    const value = Number(num) || 0;
    const [dollars, cents] = value.toFixed(2).split(".");
    const withCommas = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return (keepCents && cents !== "00") ? `$${withCommas}.${cents}` : `$${withCommas}`;
  }
  return formatPrice(num, keepCents);
};

//Price a plan in the chosen currency & billing period (null if it isn't sold that way)
export const planPrice = (plan, currency, interval) => {
  if (!plan) {
    return null;
  }
  const usd = currency === "USD";
  if (plan.type === "Program") {
    return usd ? plan.priceUsd : plan.price;
  }
  if (interval === "Annual") {
    return usd ? plan.priceUsdAnnual : plan.priceAnnual;
  }
  return usd ? plan.priceUsd : plan.price;
};

//"12 min ago", "3 days ago"
export const timeAgo = (date) => {
  const seconds = Math.round((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) {
    return "just now";
  }
  const units = [["year", 31536000], ["month", 2592000], ["week", 604800], ["day", 86400], ["hour", 3600], ["min", 60]];
  for (const [name, size] of units) {
    const n = Math.floor(seconds / size);
    if (n >= 1) {
      return `${n} ${name}${(n === 1 || name === "min") ? "" : "s"} ago`;
    }
  }
  return "";
};

//YYYY-MM-DD for date inputs
export const toInputDate = (date) => {
  if (!date) {
    return "";
  }
  const d = new Date(date);
  const pad = (n) => (n < 10 ? "0" + n : "" + n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

//YYYY-MM-DDTHH:mm for datetime-local inputs
export const toInputDateTime = (date) => {
  if (!date) {
    return "";
  }
  const d = new Date(date);
  const pad = (n) => (n < 10 ? "0" + n : "" + n);
  return `${toInputDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const youTubeThumb = (id, quality="hqdefault") => (
  `https://i.ytimg.com/vi/${id}/${quality}.jpg`
);

//Same rules as the server - accepts a full link or a bare ID
export const parseYouTubeId = (input) => {
  if (!input) {
    return null;
  }
  const value = input.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(value)) {
    return value;
  }
  const match = value.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
};

//Features are stored one per line
export const splitLines = (text) => (
  (text || "").split(/\r?\n/).map((x) => x.trim()).filter(Boolean)
);

//Will not trigger the callback until it hasn't been called at least 'wait' milliseconds
export const debounce = (callback, wait) => {
  let timeoutId = null;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback.apply(null, args);
      timeoutId = null;
    }, wait);
  };
};

export const pluralise = (n, word, plural=null) => (
  `${n} ${(n === 1) ? word : (plural || word + "s")}`
);
